/* Divetta Spatial Editorial 2.0.
 * Existing controllers own active product, variant, forms, film and scrolling.
 * This owner changes presentation only. It moves existing descriptive nodes into
 * reversible disclosures on desktop; it NEVER clones media or product forms.
 * Capability principles: REPO-CAP-0856 / 0868 / 1216, translated to native APIs.
 */
(() => {
  'use strict';
  const KEY = '__LDOrbitVisual';
  if (window[KEY]?.version === 2) { window[KEY].scan(); return; }
  window[KEY]?.destroy?.();
  const controller = new AbortController();
  const {signal} = controller;
  const desktop = matchMedia('(min-width: 990px)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();
  let enabled = true;
  let suspended = document.hidden;
  const attribute = 'data-ld-orbit-skin';
  const restore = (node, key, value) => value === null ? node.removeAttribute(key) : node.setAttribute(key, value);

  function candidates() {
    const route = document.body?.dataset.r7Route;
    if (route === 'collection') {
      if (!desktop.matches) return [];
      return [
        ...Array.from(document.querySelectorAll('[data-ldx-capelli-products]'), root => ({root, kind:'collection', hosts:'.ldx-capelli-stage__media-layer', controls:'.ldx-capelli-products__header'})),
        ...Array.from(document.querySelectorAll('[data-ld-estate-story]'), root => ({root, kind:'estate', hosts:'.ld-estate-story__media', controls:'.ld-estate-story__intro-copy'}))
      ];
    }
    if (route === 'product') return Array.from(document.querySelectorAll('.ld-commerce-pdp[data-ld-pdp-engine]'), root => ({root, kind:'pdp', hosts:'.ld-commerce-gallery__slide', controls:'.ld-commerce-gallery'}));
    if (route === 'home') return Array.from(document.querySelectorAll('[data-ld-visual-category]'), root => ({root, kind:'home', hosts:'.ld-home-film__editorial-image-frame', controls:'.ld-home-film__image-column'}));
    return [];
  }

  function sync() {
    const running = enabled && !reduced.matches && !suspended;
    instances.forEach(state => {
      state.root.setAttribute('data-ld-orbit-running', String(running));
      if (state.button) {
        state.button.hidden = reduced.matches;
        state.button.setAttribute('aria-pressed', String(enabled));
        state.button.textContent = enabled ? 'Pausa effetti' : 'Attiva effetti';
        state.button.setAttribute('aria-label', `${state.button.textContent}: anelli decorativi`);
      }
    });
  }

  function queueMeasure(state) {
    if (state.frame || state.abort.signal.aborted || suspended) return;
    state.frame = requestAnimationFrame(() => {
      state.frame = 0;
      if (state.abort.signal.aborted || !state.root.isConnected) return;
      // Read once before all writes. Decorative dimensions cannot affect host size.
      const measurements = Array.from(state.hosts, ([host]) => ({host, rect:host.getBoundingClientRect()}));
      const spreadWidth = state.spread?.getBoundingClientRect().width || 0;
      for (const {host,rect} of measurements) {
        if (rect.width <= 0 || rect.height <= 0) continue;
        const size = `${Math.round(Math.min(rect.width,rect.height) * .98)}px`;
        if (host.style.getPropertyValue('--ld-orbit-diameter') !== size) host.style.setProperty('--ld-orbit-diameter',size);
      }
      if (state.spread && spreadWidth > 0) {
        const layout = spreadWidth < 720 ? 'stack' : 'split';
        if (state.spread.dataset.ldOrbitLayout !== layout) state.spread.dataset.ldOrbitLayout = layout;
      }
    });
  }

  function decorateHost(state, host) {
    if (state.hosts.has(host) || !host.querySelector('img')) return;
    const art = document.createElement('span');
    art.className = 'ld-orbit-art';
    art.setAttribute('aria-hidden', 'true');
    for (const name of ['disc','outer','inner']) {
      const span = document.createElement('span');
      span.className = `ld-orbit-art__${name}`;
      art.append(span);
    }
    state.hosts.set(host, {
      art, before:host.getAttribute('data-ld-orbit-host'), inview:host.getAttribute('data-ld-orbit-inview'),
      diameter:host.style.getPropertyValue('--ld-orbit-diameter'), priority:host.style.getPropertyPriority('--ld-orbit-diameter'),
      hadStyle:host.hasAttribute('style')
    });
    host.setAttribute('data-ld-orbit-host', state.kind);
    host.append(art);
    if (state.observer) state.observer.observe(host);
    else host.setAttribute('data-ld-orbit-inview', 'false');
    state.resize?.observe(host);
  }

  function move(state, node, target) {
    const marker = document.createComment('ld-orbit-return');
    node.before(marker);
    state.moves.push({node,marker});
    target.append(node);
  }

  function disclosure(state, parent, nodes) {
    const content = nodes.filter(node => node && node.textContent.trim());
    if (!parent || !content.length) return;
    const details = document.createElement('details');
    details.className = 'ld-orbit-details';
    const summary = document.createElement('summary');
    summary.textContent = 'Formula, uso e certificazioni';
    const body = document.createElement('div');
    body.className = 'ld-orbit-details__body';
    details.append(summary,body);
    parent.append(details);
    content.forEach(node => move(state,node,body));
    state.groups.push(details);
    details.addEventListener('toggle', () => {
      if (state.abort.signal.aborted) return;
      queueMeasure(state);
      window.DivettaZorge?.requestFrame?.();
    }, {signal:state.abort.signal});
  }

  function organize(state) {
    if (state.kind === 'collection') {
      state.root.querySelectorAll('.ldx-capelli-chapter__card').forEach(card => {
        disclosure(state, card, Array.from(card.children).filter(node => node.matches('.ld-capelli-mobile-ingredients,.ld-product-proof--collection,.ldx-capelli-chapter__facts')));
      });
    } else if (state.kind === 'estate') {
      state.root.querySelectorAll('.ld-estate-story__content').forEach(card => {
        const explanations = Array.from(card.querySelectorAll('.ld-estate-story__explanations>.ld-estate-story__explanation'));
        const verified = card.querySelector('.ld-estate-story__verified-data');
        // Keep the beneficiary visible beside price, never rewrite its source text.
        const fit = verified && Array.from(verified.children).find(node => node.firstElementChild?.textContent.trim() === 'PER TE SE');
        if (fit) {
          const wrapper = document.createElement('div');
          wrapper.className = 'ld-orbit-fit';
          card.append(wrapper);state.groups.push(wrapper);move(state,fit,wrapper);
        }
        disclosure(state, card, [...explanations.slice(1), verified]);
      });
    }
  }

  function mount(config) {
    const {root, kind, controls, hosts} = config;
    let state = instances.get(root);
    if (state) { root.querySelectorAll(hosts).forEach(host => decorateHost(state, host)); queueMeasure(state); return; }
    const abort = new AbortController();
    state = {root,kind,abort,hosts:new Map(),observer:null,resize:null,button:null,frame:0,moves:[],groups:[],
      before:root.getAttribute(attribute),running:root.getAttribute('data-ld-orbit-running'),
      spread:kind === 'home' ? root.querySelector('[data-ld-editorial-spread]') : null};
    state.spreadBefore = state.spread?.getAttribute('data-ld-orbit-layout') ?? null;
    if ('IntersectionObserver' in window) {
      state.observer = new IntersectionObserver(entries => {
        if (abort.signal.aborted) return;
        for (const entry of entries) entry.target.setAttribute('data-ld-orbit-inview', String(entry.isIntersecting));
      }, {threshold:.01});
    }
    if ('ResizeObserver' in window) state.resize = new ResizeObserver(() => queueMeasure(state));
    root.setAttribute(attribute, kind);
    instances.set(root,state);
    root.querySelectorAll(hosts).forEach(host => decorateHost(state,host));
    if (state.spread) state.resize?.observe(state.spread);
    organize(state);
    if (state.hosts.size) {
      const target = root.querySelector(controls);
      if (target) {
        const button = document.createElement('button');
        button.type = 'button';button.className = 'ld-orbit-toggle';
        button.addEventListener('click', () => { enabled = !enabled; sync(); }, {signal:abort.signal});
        target.append(button);state.button = button;
      }
    }
    queueMeasure(state);
  }

  function unmount(root) {
    const state = instances.get(root);
    if (!state) return;
    state.abort.abort();state.observer?.disconnect();state.resize?.disconnect();cancelAnimationFrame(state.frame);
    state.button?.remove();
    for (const {node,marker} of state.moves.reverse()) {
      if (marker.parentNode) marker.replaceWith(node);
    }
    state.groups.forEach(node => node.remove());
    state.hosts.forEach(({art,before,inview,diameter,priority,hadStyle},host) => {
      art.remove();restore(host,'data-ld-orbit-host',before);restore(host,'data-ld-orbit-inview',inview);
      if (diameter) host.style.setProperty('--ld-orbit-diameter',diameter,priority);
      else host.style.removeProperty('--ld-orbit-diameter');
      if (!hadStyle && !host.getAttribute('style')?.trim()) host.removeAttribute('style');
    });
    if (state.spread) restore(state.spread,'data-ld-orbit-layout',state.spreadBefore);
    restore(root,attribute,state.before);restore(root,'data-ld-orbit-running',state.running);
    instances.delete(root);
  }
  function scan() {
    const configs = candidates();
    const wanted = new Set(configs.map(c => c.root));
    for (const root of instances.keys()) if (!wanted.has(root) || !root.isConnected) unmount(root);
    configs.forEach(mount);sync();
  }
  desktop.addEventListener('change',scan,{signal});
  reduced.addEventListener('change',sync,{signal});
  window.addEventListener('resize',() => instances.forEach(queueMeasure),{passive:true,signal});
  document.addEventListener('visibilitychange',() => { suspended=document.hidden;sync();if(!suspended)instances.forEach(queueMeasure); },{signal});
  window.addEventListener('pagehide',() => { suspended=true;instances.forEach(state=>{cancelAnimationFrame(state.frame);state.frame=0;});sync(); },{signal});
  window.addEventListener('pageshow',() => { suspended=document.hidden;scan(); },{signal});
  document.addEventListener('shopify:section:load',scan,{signal});
  document.addEventListener('shopify:section:unload',event => {
    for (const root of instances.keys()) if (event.target===root || event.target.contains?.(root)) unmount(root);
  },{signal});
  window[KEY]=Object.freeze({version:2,scan,destroy(){controller.abort();Array.from(instances.keys()).forEach(unmount);delete window[KEY];}});
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',scan,{once:true,signal});else scan();
})();
